import { Icons, defaultTheme, useHelmet } from '@agro-club/agroclub-shared'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import { AAntdTabs, AButton } from 'views/components/Analytics'
import { FC, useCallback, useEffect, useState } from 'react'
import { useAnalyticsContext } from 'analytics/hooks'
import * as Layout from 'views/layouts/NewLayout'
import * as Header from 'views/ui/Header/Header'
import { useMediaQuery } from 'react-responsive'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { useNavigate } from 'react-router'
import { useParams } from 'react-router'
import UsContractsRoutes from '../routes'
import { UsContractsFilters } from './UsContractsFilters'
import { OpenBoard } from './OpenBoard'
import { UsContractAdd } from '../Modals/UsContractAdd'
import { Tab, useTab, useTabsDataV2 } from 'hooks/useTabsDataV2'
import { UsContractsTabs } from 'modules/domain/usContracts/types'
import { PAGE_SIZE_1K } from 'modules/constants'
import { ClosedBoard } from './ClosedBoard'

const nonEditableParams = { pageSize: PAGE_SIZE_1K }

export const UsContracts: FC = () => {
  const { t } = useTranslation('usContracts')
  const { setOptions } = useAnalyticsContext()
  const [showModal, setShowModal] = useState(false)
  useHelmet({ title: t('title') })
  const navigate = useNavigate()

  const { activeTab: activeTabParam, selected } = useParams()
  const [activeTab, setActiveTab] = useState(activeTabParam || UsContractsTabs.open)
  const [selectedContractId, setSelectedContractId] = useState(isNaN(parseInt(selected)) ? undefined : Number(selected))

  const tabs: Record<string, Tab> = {}

  tabs[UsContractsTabs.open] = useTab({
    label: t('opened'),
    nonEditableParams,
  })

  tabs[UsContractsTabs.closed] = useTab({
    label: t('closed'),
    //initialParams: { filter: { month: currMonth, source: RequestType.internal } },
    nonEditableParams,
  })
  const { isFiltersLoaded, updateCommonFilterState } = useTabsDataV2({
    activeTab,
    tabs,
    independTabs: true,
  })

  const changeRoute = useCallback(() => {
    navigate(
      {
        pathname: generatePath(UsContractsRoutes.ListWithTabAndSelected, {
          activeTab: activeTab,
          selected: selectedContractId ?? 'all',
        }),
        search: location.search,
      },
      { replace: true },
    )
    setOptions({
      place: activeTab,
    })
  }, [activeTab, navigate, selectedContractId, setOptions])

  useEffect(() => {
    changeRoute()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedContractId])

  const changeActiveTab = useCallback((tab) => {
    setActiveTab(tab)
    setSelectedContractId(undefined)
  }, [])

  const isMobile = useMediaQuery({
    query: `(max-width: ${defaultTheme.queries.mobile})`,
  })

  return (
    <APageWrapper page="UsContracts" place="list">
      <Layout.WrapperContent>
        <Layout.Header>
          {!isMobile && (
            <Layout.TopHeader>
              <Header.Breadcrumbs routes={[{ breadcrumbName: t('title') }]} />
              <AButton id="add" color="approve" Icon={Icons.IconAdd} onClick={() => setShowModal(true)}>
                {t('addContract')}
              </AButton>
            </Layout.TopHeader>
          )}

          <Layout.PageName>{t('title')}</Layout.PageName>

          <UsContractsFilters
            setListRequestParams={tabs[activeTab].setter}
            listRequestParams={tabs[activeTab].params}
            isFiltersLoaded={isFiltersLoaded}
            updateFilterState={tabs[activeTab].updateFilterState}
          />
        </Layout.Header>
        <AAntdTabs
          id="tabs"
          items={[
            {
              label: t('opened'),
              key: UsContractsTabs.open,
              children: (
                <OpenBoard
                  listRequestParams={tabs[UsContractsTabs.open].params}
                  isFiltersLoaded={isFiltersLoaded}
                  setSelectedContractId={setSelectedContractId}
                  selectedContractId={selectedContractId}
                  updateCommonFilterState={updateCommonFilterState}
                />
              ),
            },
            {
              label: t('canceled'),
              key: UsContractsTabs.closed,
              children: (
                <ClosedBoard
                  listRequestParams={tabs[UsContractsTabs.closed].params}
                  isFiltersLoaded={isFiltersLoaded}
                  setSelectedContractId={setSelectedContractId}
                  selectedContractId={selectedContractId}
                />
              ),
            },
          ]}
          onChange={changeActiveTab}
          activeKey={activeTab}
        />
        {showModal && (
          <UsContractAdd
            refetch={async () => tabs[UsContractsTabs.open].updateFilterState({})}
            onClose={() => setShowModal(false)}
          />
        )}
      </Layout.WrapperContent>
    </APageWrapper>
  )
}
